import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import type { RemoveInterviewerKnowledgePayload, SourceRemovalPreview } from '@/lib/api/onboarding';

const formatOpenQuestion = (question: Record<string, any>) =>
  question.question || question.title || question.topic_name || JSON.stringify(question);

interface KnowledgeRemovalDialogProps {
  organizationId: string;
  interviewerId: string;
  sourceLabel: string;
  payload: RemoveInterviewerKnowledgePayload | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: React.ReactNode;
  previewLoadingText?: string;
  confirmLabel?: string;
  confirmVariant?: 'default' | 'destructive';
  onRemoved?: () => Promise<void> | void;
}

export function KnowledgeRemovalDialog({
  organizationId,
  interviewerId,
  sourceLabel,
  payload,
  open,
  onOpenChange,
  title = 'Remove Knowledge Source',
  description,
  previewLoadingText = 'Reviewing what Agent Knowledge would weaken if this source is removed...',
  confirmLabel = 'Remove Source',
  confirmVariant = 'destructive',
  onRemoved,
}: KnowledgeRemovalDialogProps) {
  const {
    previewKnowledgeRemoval,
    removeKnowledge,
    isPreviewingKnowledgeRemoval,
    isRemovingKnowledge,
  } = useInterviewerSetup(organizationId);
  const [preview, setPreview] = useState<SourceRemovalPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!open || !payload) {
        setPreview(null);
        setPreviewError(null);
        return;
      }

      setPreview(null);
      setPreviewError(null);
      const nextPreview = await previewKnowledgeRemoval(interviewerId, payload);
      if (cancelled) return;

      if (nextPreview) {
        setPreview(nextPreview);
      } else {
        setPreviewError('I could not preview the impact of removing this source. Please try again.');
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, payload, previewKnowledgeRemoval, interviewerId]);

  const isBusy = isPreviewingKnowledgeRemoval || isRemovingKnowledge;

  const handleConfirm = async () => {
    if (!payload || isBusy) return;

    const result = await removeKnowledge(interviewerId, payload);
    if (!result) return;

    if (onRemoved) {
      try {
        await onRemoved();
      } catch (error) {
        setPreviewError(error instanceof Error ? error.message : 'I could not finish removing this source.');
        return;
      }
    }

    setPreview(null);
    setPreviewError(null);
    onOpenChange(false);
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (isRemovingKnowledge) return;
    if (!nextOpen) {
      setPreview(null);
      setPreviewError(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description || (
              <>
                Remove <span className="font-medium text-foreground">{sourceLabel}</span> from Agent Knowledge.
                I&apos;ll rebuild from the remaining sources after you confirm.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isPreviewingKnowledgeRemoval && !preview ? (
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{previewLoadingText}</p>
          </div>
        ) : null}

        {previewError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        ) : null}

        {preview ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="text-sm font-medium">Expected impact</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {preview.prompt_impact?.summary || 'I will rebuild Agent Knowledge without this source.'}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">Current agent guidance</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {Object.keys(preview.current_profile.confirmed_topic_summaries || {}).length} knowledge area
                  {Object.keys(preview.current_profile.confirmed_topic_summaries || {}).length === 1 ? '' : 's'} right now.
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">Knowledge at risk</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {preview.knowledge_at_risk.length > 0
                    ? `${preview.knowledge_at_risk.length} knowledge area${preview.knowledge_at_risk.length === 1 ? '' : 's'} may weaken or disappear.`
                    : 'No knowledge areas are expected to disappear.'}
                </p>
              </div>
            </div>

            {preview.knowledge_at_risk.length > 0 ? (
              <div className="space-y-3 rounded-lg border p-4">
                <p className="text-sm font-medium">What may be removed</p>
                <div className="space-y-3">
                  {preview.knowledge_at_risk.map((item) => (
                    <div key={`${item.topic_id}-${item.change_type}`} className="rounded-lg border bg-muted/10 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium">{item.topic_name}</p>
                        <Badge variant="outline">{item.change_type}</Badge>
                      </div>
                      {item.current_summary ? (
                        <p className="mt-2 text-sm text-muted-foreground">{item.current_summary}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {preview.reopened_questions.length > 0 ? (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                <p className="text-sm font-medium">Questions that may reopen</p>
                <div className="space-y-2">
                  {preview.reopened_questions.map((question, index) => (
                    <p key={`${formatOpenQuestion(question)}-${index}`} className="text-sm text-muted-foreground">
                      {formatOpenQuestion(question)}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={isRemovingKnowledge}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={handleConfirm} disabled={!preview || isBusy}>
            {isRemovingKnowledge ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rebuilding...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                {confirmLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
